import cx from "classnames";
import { ReactComponent as Delete } from "assets/icons/delete.svg";
import { ReactComponent as Edit } from "assets/icons/edit.svg";
import { useRole } from "hooks/common";
import { useLessonSettings } from "hooks/redux";
import { IconButton } from "components/Interactive/IconButton";
import { ChapterNameText } from "components/Lesson";
import type { TCourseLessonChapter } from "types/api/course";
import "./ChaptersSidebarItem.styles.scss";

type ChapterSidebarItemProps = {
  /**
   * Lesson chapter data to display.
   */
  chapter: TCourseLessonChapter;
  /**
   * Indicates active state of chapter list item.
   * @default false
   */
  active?: boolean;
  /**
   * Callback function fires on chapter item click to set active state.
   * @param chapterId current active chapter ID.
   * @returns {void} nothing.
   */
  onChapterClick?: (chapterId: number) => void;
  /**
   * Callback function fires on chapter item edit button click.
   * @param chapter current active chapter data.
   * @returns {void} nothing.
   */
  onChapterEditClick?: (chapter: TCourseLessonChapter) => void;
  /**
   * Callback function fires on chapter item delete button click.
   * @param chapterId current active chapter data.
   * @returns {void} nothing.
   */
  onChapterDeleteClick?: (chapter: TCourseLessonChapter) => void;
};

export const ChapterSidebarItem: React.FC<ChapterSidebarItemProps> = (
  props
) => {
  const {
    chapter,
    active = false,
    onChapterClick,
    onChapterEditClick,
    onChapterDeleteClick,
  } = props;

  const { viewMode, isExerciseLiveMode } = useLessonSettings();
  const { isUserAdmin } = useRole();

  const chapterButtonClickHandler = () => {
    onChapterClick?.(chapter.id);
  };

  const chapterEditButtonClickHandler = () => {
    onChapterEditClick?.(chapter);
  };

  const chapterDeleteButtonClickHandler = () => {
    onChapterDeleteClick?.(chapter);
  };

  return (
    <li
      className={cx([
        "nb-lesson-chapters-sidebar-item",
        {
          ["nb-lesson-chapters-sidebar-item--active"]: active,
        },
      ])}
    >
      <button
        type="button"
        title={chapter.name}
        className="nb-lesson-chapters-sidebar-item-button"
        onClick={chapterButtonClickHandler}
      >
        <ChapterNameText text={chapter.name} />
      </button>
      {viewMode === "constructor" && !isExerciseLiveMode && isUserAdmin && (
        <IconButton
          icon={<Edit />}
          variant="tertiary"
          onClick={chapterEditButtonClickHandler}
        />
      )}
      {viewMode === "constructor" && !isExerciseLiveMode && isUserAdmin && (
        <IconButton
          icon={<Delete />}
          variant="tertiary"
          onClick={chapterDeleteButtonClickHandler}
        />
      )}
    </li>
  );
};
